import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';
import SftGlobalData from '../library/SftGlobalData';
import Colors from '../library/Colors';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

/**
 * UploadsScreen — MyFasMan Mobile › Uploads (design artifact §4).
 *
 * Client files the mobile POS prints/displays, managed per scope:
 * '0000' global · 'SS00' store · 'SSPP' register (the kasvar location codes).
 * Tabs: ticket logo, invoice logo, invoice .fr3 forms (+ ReportFactuur1
 * assignment grid), label layouts (+ designer entry), lock screen (soon).
 *
 * SoftTouch users only — every rank incl. supp; the server enforces the same
 * gate per action.
 */
const UploadsScreen = (props) => {
    const navigate = useNavigate();

    // The tab survives a round-trip to the designer (back used to always
    // land on the first tab).
    const [tab, setTabState] = useState(() => {
        const stored = parseInt(window.sessionStorage.getItem('upl_tab') || '0', 10);
        return isNaN(stored) ? 0 : Math.min(4, Math.max(0, stored));
    });
    const setTab = (value) => {
        window.sessionStorage.setItem('upl_tab', String(value));
        setTabState(value);
    };
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [assets, setAssets] = useState(null);
    const [forms, setForms] = useState(null);

    // Upload dialog state (images).
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgKind, setDlgKind] = useState('ticket_logo');
    const [dlgScopeType, setDlgScopeType] = useState('global'); // global|store|register
    const [dlgStore, setDlgStore] = useState('');
    const [dlgRegister, setDlgRegister] = useState('');
    const [dlgImage, setDlgImage] = useState('');

    // Assignment dialog state (ReportFactuur1).
    const [asgOpen, setAsgOpen] = useState(false);
    const [asgScopeType, setAsgScopeType] = useState('global');
    const [asgStore, setAsgStore] = useState('');
    const [asgRegister, setAsgRegister] = useState('');
    const [asgValue, setAsgValue] = useState('');

    const imgFileRef = useRef(null);
    const fr3FormRef = useRef(null);
    const fr3LabelRef = useRef(null);

    const isSft = props.isSft === true;
    const t = props.t || ((k, d) => d || k);
    // Sidebar-aware content class (same contract as the other screens:
    // .expanded clears the drawer, .not_expanded is the mobile layout).
    const containerClass = (props.screenWidth < 600 ? 'not_expanded ' : 'expanded ')
        + 'container_content _80perc ' + props.theme + ' container_content_' + props.theme;
    const isDark = props.theme === 'dark';
    const panelBorder = isDark ? Colors.dark_accent_darker : Colors.light_accent_darker;
    const inkFaint = isDark ? Colors.dark_text_accent : '#6B7280';

    const notify = (text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
    };

    // ------------------------------------------------------------------
    //  DATA
    // ------------------------------------------------------------------

    const reload = useCallback(() => {
        if (props.api === '' || !isSft) return;
        SftGlobalData.post('uploads&action=listAssets',
            { key: props.api, sftUserId: props.sftUserId }).then((response) => {
            if (response?.success) setAssets(response.data);
            setLoading(false);
        });
        SftGlobalData.post('uploads&action=listInvoiceForms',
            { key: props.api, sftUserId: props.sftUserId }).then((response) => {
            if (response?.success) setForms(response.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, isSft]);

    useEffect(() => { reload(); }, [reload]);

    const dialogScope = (scopeType, store, register) => {
        if (scopeType === 'store') return store !== '' ? store + '00' : '';
        if (scopeType === 'register') return register;
        return '0000';
    };

    const scopeLabel = (scope) => {
        if (scope === '0000') return t('upl_global', 'Global');
        if (scope.substring(2) === '00') {
            const store = (assets?.stores || []).find((entry) => entry.code === scope.substring(0, 2));
            return t('upl_store', 'Store') + ' ' + scope.substring(0, 2) + (store ? ' — ' + store.oms : '');
        }
        const register = (assets?.registers || []).find((entry) => entry.code === scope);
        return t('upl_register', 'Register') + ' ' + scope + (register ? ' — ' + register.oms : '');
    };

    // ------------------------------------------------------------------
    //  ACTIONS
    // ------------------------------------------------------------------

    const openUploadDialog = (kind) => {
        setDlgKind(kind);
        setDlgScopeType('global');
        setDlgStore('');
        setDlgRegister('');
        setDlgImage('');
        setDlgOpen(true);
    };

    const submitUpload = () => {
        const scope = dialogScope(dlgScopeType, dlgStore, dlgRegister);
        if (scope === '' || dlgImage === '') return;
        setBusy(true);
        SftGlobalData.post('uploads&action=uploadAsset', {
            key: props.api, sftUserId: props.sftUserId,
            kind: dlgKind, scope, image: dlgImage,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setDlgOpen(false);
                notify(t('upl_uploaded', 'Uploaded'), true);
                reload();
            } else {
                notify(response?.message || 'Upload failed', false);
            }
        });
    };

    const deleteAsset = (kind, scope) => {
        if (!window.confirm(t('upl_confirm_delete', 'Delete this file?'))) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=deleteAsset', {
            key: props.api, sftUserId: props.sftUserId, kind, scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Delete failed', false);
        });
    };

    /** Shared .fr3 uploader: reads bytes, base64-encodes (1252-safe transport). */
    const uploadFr3 = (e, action) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!/\.fr3$/i.test(file.name)) {
            notify(t('upl_fr3_only', 'Only .fr3 files — FastReport 2 (.frf) forms only render on Windows MySales'), false);
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            setBusy(true);
            SftGlobalData.post('uploads&action=' + action, {
                key: props.api, sftUserId: props.sftUserId,
                name: file.name, content: btoa(binary),
            }).then((response) => {
                setBusy(false);
                if (response?.success) {
                    notify(t('upl_uploaded', 'Uploaded'), true);
                    reload();
                } else {
                    notify(response?.message || 'Upload failed', false);
                }
            });
        };
        reader.readAsArrayBuffer(file);
    };

    const deleteFr3 = (action, name) => {
        if (!window.confirm(t('upl_confirm_delete', 'Delete this file?') + ' (' + name + ')')) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=' + action, {
            key: props.api, sftUserId: props.sftUserId, name,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Delete failed', false);
        });
    };

    /**
     * One-time migration of a repid invoice layout: the server copies it
     * from the shared reports2 store into the client's custom dir, after
     * which it edits like any other invoice form.
     */
    const importReportForm = (name) => {
        if (!window.confirm(t('upl_confirm_fetch',
            'Copy this layout into the client\'s custom reports so it can be edited?') + ' (' + name + ')')) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=importReportForm', {
            key: props.api, sftUserId: props.sftUserId, name,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Fetch failed', false);
        });
    };

    const submitAssignment = () => {
        const scope = dialogScope(asgScopeType, asgStore, asgRegister);
        if (scope === '') return;
        setBusy(true);
        SftGlobalData.post('uploads&action=assignInvoiceForm', {
            key: props.api, sftUserId: props.sftUserId, scope, value: asgValue,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setAsgOpen(false);
                notify(t('upl_assigned', 'Assignment saved'), true);
                reload();
            } else {
                notify(response?.message || 'Assignment failed', false);
            }
        });
    };

    const clearAssignment = (scope) => {
        setBusy(true);
        SftGlobalData.post('uploads&action=clearInvoiceAssignment', {
            key: props.api, sftUserId: props.sftUserId, scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Failed', false);
        });
    };

    const pickImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setDlgImage(ev.target.result);
        reader.readAsDataURL(file);
    };

    // ------------------------------------------------------------------
    //  RENDER PIECES
    // ------------------------------------------------------------------

    const fileRow = (children) => (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
            border: '1px solid ' + panelBorder, borderRadius: 2, p: 1.2, mb: 1,
            backgroundColor: isDark ? Colors.dark_accent : '#FFFFFF',
        }}>
            {children}
        </Box>
    );

    const smallBtn = (label, onClick, danger, solid) => (
        <Button size="small" variant={solid ? 'contained' : 'outlined'} disabled={busy} onClick={onClick}
            sx={{
                fontSize: 12.5, textTransform: 'none', py: 0.35, px: 1.4, minWidth: 0,
                borderColor: danger ? Colors.def_red : Colors.sft_blue,
                color: solid ? '#fff' : (danger ? Colors.def_red : Colors.sft_blue),
                backgroundColor: solid ? Colors.sft_blue : undefined,
            }}>
            {label}
        </Button>
    );

    const scopeChip = (scope) => (
        <Chip size="small" label={scopeLabel(scope)}
            sx={{ fontSize: 12, fontWeight: 700, backgroundColor: '#E1EFF8', color: '#075985' }} />
    );

    /** One image kind tab: existing files + inheriting stores + upload. */
    const imageKindPane = (kind, intro) => {
        const files = (assets?.kinds && assets.kinds[kind]) ? assets.kinds[kind] : [];
        const coveredStores = {};
        files.forEach((file) => {
            if (file.scope !== '0000' && file.scope.substring(2) === '00') coveredStores[file.scope.substring(0, 2)] = true;
        });
        const hasGlobal = files.some((file) => file.scope === '0000');

        return (
            <div>
                {intro && (
                    <Box sx={{ mb: 1.2, p: 1.2, borderRadius: 1.5, backgroundColor: '#FDF6E3', border: '1px solid #F0E1B8', color: '#7A5D00', fontSize: 13.5 }}>
                        {intro}
                    </Box>
                )}
                {files.map((file) => (
                    <React.Fragment key={kind + file.scope}>
                        {fileRow(
                            <React.Fragment>
                                <img src={file.url + '?v=' + Date.now()} alt={file.scope}
                                    style={{ width: 84, height: 34, objectFit: 'contain', border: '1px solid ' + panelBorder, borderRadius: 4, background: '#fff' }} />
                                {scopeChip(file.scope)}
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{file.file.split('/').pop()}</Typography>
                                <Box sx={{ flex: 1 }} />
                                {smallBtn(t('upl_delete', 'Remove'), () => deleteAsset(kind, file.scope), true)}
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ))}
                {!hasGlobal && files.length === 0 && (
                    <Typography sx={{ fontSize: 13.5, color: inkFaint, mb: 1 }}>
                        {t('upl_no_files', 'No files yet — the app falls back to the current (legacy) behaviour.')}
                    </Typography>
                )}
                {(assets?.stores || []).filter((store) => !coveredStores[store.code]).map((store) => (
                    <React.Fragment key={'inh' + store.code}>
                        {fileRow(
                            <React.Fragment>
                                <Typography sx={{ fontSize: 13, color: inkFaint }}>
                                    {t('upl_store', 'Store')} {store.code} — {store.oms} · {hasGlobal ? t('upl_inherits_global', 'inherits Global') : t('upl_inherits_legacy', 'inherits the legacy file')}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'center', border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, mt: 1 }}>
                    {smallBtn('＋ ' + t('upl_upload_scope', 'Upload for scope… (Global / Store / Register)'), () => openUploadDialog(kind), false, true)}
                </Box>
            </div>
        );
    };

    // ------------------------------------------------------------------
    //  GATES
    // ------------------------------------------------------------------

    if (!isSft) {
        return (
            <div className={containerClass}>
                <Typography sx={{ p: 4, textAlign: 'center' }}>
                    {t('upl_sft_only', 'This section is only available to SoftTouch users.')}
                </Typography>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={containerClass}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
                </Box>
            </div>
        );
    }

    // ------------------------------------------------------------------
    //  SCREEN
    // ------------------------------------------------------------------

    return (
        <div className={containerClass}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" sx={{ textTransform: 'none', color: Colors.sft_blue }}
                    onClick={() => navigate('/' + props.lang + '/mobile')}>
                    ‹ {t('upl_back_modules', 'Back to modules')}
                </Button>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>MyFasMan Mobile › {t('upl_uploads', 'Uploads')}</Typography>
                <Box sx={{ flex: 1 }} />
                {/* The manifest is what the DEVICES read; regenerate publishes
                    it without needing a new upload, the link verifies it. */}
                {smallBtn(t('upl_sync_manifest', 'Sync manifest'), () => {
                    setBusy(true);
                    SftGlobalData.post('uploads&action=regenerateManifest', {
                        key: props.api, sftUserId: props.sftUserId,
                    }).then((response) => {
                        setBusy(false);
                        if (response?.success) notify(t('upl_manifest_synced', 'Manifest published'), true);
                        else notify(response?.message || 'Failed', false);
                    });
                })}
                {assets?.manifestUrl && (
                    <a href={assets.manifestUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize: 13.5, color: Colors.sft_blue }}>
                        manifest.json ↗
                    </a>
                )}
            </Box>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontSize: 14.5, minHeight: 44 }, minHeight: 44 }}>
                <Tab label={t('upl_tab_ticket', 'Ticket logo')} />
                <Tab label={t('upl_tab_invoice_logo', 'Invoice logo')} />
                <Tab label={t('upl_tab_invoice_forms', 'Invoice forms')} />
                <Tab label={t('upl_tab_labels', 'Label layouts')} />
                <Tab label={t('upl_tab_lock_live', 'Lock screen')} />
            </Tabs>

            {tab === 0 && imageKindPane('ticket_logo',
                t('upl_ticket_intro', 'Printed on receipts and ZPL labels. The global upload also refreshes the legacy file older app versions use.'))}

            {tab === 1 && imageKindPane('invoice_logo',
                t('upl_invoice_logo_intro', 'Print order on invoices: invoice logo → client logo (licence module) → ticket logo. An empty tab still prints the client logo, like today.'))}

            {tab === 4 && imageKindPane('lock_screen',
                t('upl_lock_intro', 'The BACKGROUND image of the POS lock screen (the logo on it stays the ticket logo). Recommended: 1920×1080 px (16:9, landscape) — center-cropped to fill the screen, so portrait devices crop the sides. Max 4 MB. Without an upload the current built-in background is used, like today.'))}

            {tab === 2 && forms && (
                <div>
                    {(forms.files || []).map((name) => (
                        <React.Fragment key={name}>
                            {fileRow(
                                <React.Fragment>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{name}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: inkFaint }}>reports2 · {t('upl_custom_dir', 'client dir')}</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {smallBtn(t('upl_touchup_btn', 'Touch-up'), () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(name)))}
                                    {smallBtn(t('upl_delete', 'Delete'), () => deleteFr3('deleteInvoiceForm', name), true)}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, my: 1, flexWrap: 'wrap' }}>
                        {smallBtn('＋ ' + t('upl_upload_fr3', 'Upload .fr3…'), () => { if (fr3FormRef.current) fr3FormRef.current.click(); }, false, true)}
                        <Typography sx={{ fontSize: 12.5, color: inkFaint }}>
                            {t('upl_frf_hint', '(.frf refused — FastReport 2 only renders on Windows MySales)')}
                        </Typography>
                    </Box>
                    <input type="file" accept=".fr3" style={{ display: "none" }} ref={fr3FormRef} onChange={(e) => uploadFr3(e, 'uploadInvoiceForm')} />

                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, mt: 2, mb: 1 }}>
                        {t('upl_assignment_title', 'ReportFactuur1 — which invoice report each register uses')}
                    </Typography>
                    <Box sx={{ overflowX: 'auto', border: '1px solid ' + panelBorder, borderRadius: 2 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560, fontSize: 13.5 }}>
                            <thead>
                                <tr>
                                    {[t('upl_register', 'Register'), t('upl_effective', 'Effective value'), t('upl_source', 'Source'), ''].map((header) => (
                                        <th key={header} style={{ textAlign: 'left', padding: '7px 10px', borderBottom: '1.5px solid ' + panelBorder, fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1 }}>{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(forms.assignments?.registers || []).map((register) => (
                                    <tr key={register.code}>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>{register.code} — {register.oms}</td>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder, fontFamily: 'Consolas, monospace' }}>
                                            {register.effective === '' ? '—' : register.effective}
                                        </td>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>
                                            {register.source === 'none' ? '—' : scopeChip(register.source === 'global' ? '0000' : (register.source === 'store' ? register.code.substring(0, 2) + '00' : register.code))}
                                        </td>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                            {(() => {
                                                // A direct .fr3 value, or a repid whose
                                                // report_.repform layout is a CUSTOM
                                                // report in the client dir (resolved
                                                // server-side) — both open the same
                                                // invoice touch-up editor. A repid
                                                // layout still sitting only in the
                                                // shared store gets a one-time
                                                // "Fetch layout" copy into the
                                                // client dir first.
                                                const value = (register.effective || '').trim();
                                                if (/\.fr3$/i.test(value) && (forms.files || []).indexOf(value) >= 0) {
                                                    return smallBtn(t('upl_touchup_btn', 'Touch-up'),
                                                        () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(value)));
                                                }
                                                if (/^\d+$/.test(value)) {
                                                    const resolved = (forms.repid_forms ? forms.repid_forms[value] : null) || [];
                                                    return resolved.map((entry) => (
                                                        <React.Fragment key={entry.name}>
                                                            {entry.present
                                                                ? smallBtn(t('upl_touchup_btn', 'Touch-up'),
                                                                    () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(entry.name)))
                                                                : smallBtn(t('upl_fetch_layout', 'Fetch layout'),
                                                                    () => importReportForm(entry.name))}
                                                        </React.Fragment>
                                                    ));
                                                }
                                                return null;
                                            })()}
                                            {smallBtn(t('upl_change_value', 'Change…'), () => {
                                                // Prefill the assignment dialog at the
                                                // scope the effective value comes from.
                                                if (register.source === 'store') {
                                                    setAsgScopeType('store'); setAsgStore(register.code.substring(0, 2)); setAsgRegister('');
                                                } else if (register.source === 'register') {
                                                    setAsgScopeType('register'); setAsgStore(''); setAsgRegister(register.code);
                                                } else {
                                                    setAsgScopeType('global'); setAsgStore(''); setAsgRegister('');
                                                }
                                                setAsgValue(register.effective || '');
                                                setAsgOpen(true);
                                            })}
                                            {register.source === 'register' && smallBtn(t('upl_clear_override', 'Clear override'), () => clearAssignment(register.code), true)}
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        {smallBtn(t('upl_set_assignment', 'Set assignment…'), () => {
                            setAsgScopeType('global'); setAsgStore(''); setAsgRegister(''); setAsgValue(''); setAsgOpen(true);
                        }, false, true)}
                    </Box>
                </div>
            )}

            {tab === 3 && (
                <div>
                    {fileRow(
                        <React.Fragment>
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('upl_classic_zpl', 'Classic ZPL layout')}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: inkFaint }}>
                                kasvar mobileZPLcode · {t('upl_classic_hint', 'used unless a device selects an .fr3 below')}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {smallBtn(t('upl_open_designer', 'Open designer'), () => navigate('/' + props.lang + '/mobile/designer'), false, true)}
                        </React.Fragment>
                    )}
                    {(assets?.labels || []).map((file) => (
                        <React.Fragment key={file.name}>
                            {fileRow(
                                <React.Fragment>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{file.name}</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {smallBtn(t('upl_edit', 'Edit'), () => navigate('/' + props.lang + '/mobile/designer/labels/' + encodeURIComponent(file.name)))}
                                    {smallBtn(t('upl_delete', 'Delete'), () => deleteFr3('deleteLabelLayout', file.name), true)}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, mt: 1 }}>
                        {smallBtn('＋ ' + t('upl_upload_label', 'Upload .fr3 label layout…'), () => { if (fr3LabelRef.current) fr3LabelRef.current.click(); }, false, true)}
                    </Box>
                    <input type="file" accept=".fr3" style={{ display: "none" }} ref={fr3LabelRef} onChange={(e) => uploadFr3(e, 'uploadLabelLayout')} />
                </div>
            )}

            {/* Image upload dialog */}
            <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>{t('upl_upload_title', 'Upload image')}</DialogTitle>
                <DialogContent>
                    <Select fullWidth size="small" value={dlgScopeType} sx={{ mb: 1.2, fontSize: 14 }}
                        onChange={(e) => setDlgScopeType(e.target.value)}>
                        <MenuItem value="global" sx={{ fontSize: 14 }}>{t('upl_global', 'Global')}</MenuItem>
                        <MenuItem value="store" sx={{ fontSize: 14 }}>{t('upl_store', 'Store')}</MenuItem>
                        <MenuItem value="register" sx={{ fontSize: 14 }}>{t('upl_register', 'Register')}</MenuItem>
                    </Select>
                    {dlgScopeType === 'store' && (
                        <Select fullWidth size="small" value={dlgStore} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setDlgStore(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_store', 'Choose a store…')}</MenuItem>
                            {(assets?.stores || []).map((store) => (
                                <MenuItem key={store.code} value={store.code} sx={{ fontSize: 14 }}>{store.code} — {store.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {dlgScopeType === 'register' && (
                        <Select fullWidth size="small" value={dlgRegister} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setDlgRegister(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_register', 'Choose a register…')}</MenuItem>
                            {(assets?.registers || []).map((register) => (
                                <MenuItem key={register.code} value={register.code} sx={{ fontSize: 14 }}>{register.code} — {register.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {dlgImage !== '' && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.2 }}>
                            <img src={dlgImage} alt="preview" style={{ maxWidth: 260, maxHeight: 110, objectFit: 'contain', border: '1px solid #C9D2DA', borderRadius: 4 }} />
                        </Box>
                    )}
                    <Button fullWidth variant="outlined" size="small" sx={{ textTransform: 'none', fontSize: 13 }}
                        onClick={() => { if (imgFileRef.current) imgFileRef.current.click(); }}>
                        {t('upl_pick_image', 'Choose image (PNG / JPEG / BMP)…')}
                    </Button>
                    <input type="file" accept="image/png,image/jpeg,image/bmp" style={{ display: "none" }} ref={imgFileRef} onChange={pickImage} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button disabled={busy || dlgImage === '' || dialogScope(dlgScopeType, dlgStore, dlgRegister) === ''}
                        variant="contained" onClick={submitUpload}
                        sx={{ textTransform: 'none', backgroundColor: Colors.sft_blue }}>
                        {t('upl_upload', 'Upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assignment dialog */}
            <Dialog open={asgOpen} onClose={() => setAsgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>{t('upl_set_assignment', 'Set assignment…')}</DialogTitle>
                <DialogContent>
                    <Select fullWidth size="small" value={asgScopeType} sx={{ mb: 1.2, fontSize: 14 }}
                        onChange={(e) => setAsgScopeType(e.target.value)}>
                        <MenuItem value="global" sx={{ fontSize: 14 }}>{t('upl_global', 'Global')}</MenuItem>
                        <MenuItem value="store" sx={{ fontSize: 14 }}>{t('upl_store', 'Store')}</MenuItem>
                        <MenuItem value="register" sx={{ fontSize: 14 }}>{t('upl_register', 'Register')}</MenuItem>
                    </Select>
                    {asgScopeType === 'store' && (
                        <Select fullWidth size="small" value={asgStore} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setAsgStore(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_store', 'Choose a store…')}</MenuItem>
                            {(assets?.stores || []).map((store) => (
                                <MenuItem key={store.code} value={store.code} sx={{ fontSize: 14 }}>{store.code} — {store.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {asgScopeType === 'register' && (
                        <Select fullWidth size="small" value={asgRegister} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setAsgRegister(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_register', 'Choose a register…')}</MenuItem>
                            {(assets?.registers || []).map((register) => (
                                <MenuItem key={register.code} value={register.code} sx={{ fontSize: 14 }}>{register.code} — {register.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    <Select fullWidth size="small" displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                        value={(forms?.files || []).indexOf(asgValue) >= 0 || asgValue === 'TicketPrinter' ? asgValue : ''}
                        onChange={(e) => setAsgValue(e.target.value)}>
                        <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_form', 'Choose a form / option…')}</MenuItem>
                        <MenuItem value="TicketPrinter" sx={{ fontSize: 14 }}>TicketPrinter ({t('upl_ticketprinter_hint', 'print on the receipt printer')})</MenuItem>
                        {(forms?.files || []).map((name) => (
                            <MenuItem key={name} value={name} sx={{ fontSize: 14 }}>{name}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        fullWidth size="small" value={asgValue}
                        onChange={(e) => setAsgValue(e.target.value)}
                        label={t('upl_value_free', 'Or type a value (report id / .fr3 name)')}
                        inputProps={{ style: { fontSize: 14 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAsgOpen(false)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button disabled={busy || dialogScope(asgScopeType, asgStore, asgRegister) === ''}
                        variant="contained" onClick={submitAssignment}
                        sx={{ textTransform: 'none', backgroundColor: Colors.sft_blue }}>
                        {t('save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UploadsScreen;
